import { Navigate } from "react-router-dom";
import { useRole, type WorkspaceRole } from "@/hooks/useRole";

interface Props {
  /** Roles allowed to view the wrapped route. */
  allowed: WorkspaceRole[];
  /** Where to send a disallowed role (a page every role can see). */
  redirectTo?: string;
  children: React.ReactNode;
}

/**
 * Route-level role guard. Renders its children only when the current workspace
 * role is in `allowed`; otherwise redirects. This backs up the sidebar nav's
 * role filtering so a restricted role (photographer/salesperson) can't reach an
 * admin page by typing its URL — matching the legacy admin, where these pages
 * were only reachable through the owner/administrator-gated nav.
 *
 * Safe to read the role synchronously: WorkspaceLayout only mounts the routed
 * Outlet after the workspace (and thus the role) has loaded.
 */
const RequireRole = ({ allowed, redirectTo = "/admin/galleries", children }: Props) => {
  const { can } = useRole();
  if (!can(allowed)) return <Navigate to={redirectTo} replace />;
  return <>{children}</>;
};

export default RequireRole;
